import { QuickAddComponent } from '@theme/quick-add';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

// Keep Horizon's rendering/quantity/checkout code. Only replace a line's merchandise.
const shared = window.__npGiftCoordinatorV2 ||= { pending: null, stateRequest: null };
const root = window.Shopify?.routes?.root || '/';
const endpoint = path => root.replace(/\/$/, '') + '/' + path;
const propertiesKey = properties => JSON.stringify(Object.entries(properties || {}).sort(([a], [b]) => a.localeCompare(b)));
const identity = line => JSON.stringify([String(line.variant_id || line.id), propertiesKey(line.properties), String(line.selling_plan_allocation?.selling_plan?.id || '')]);
const sameOriginal = (line, model) => String(line.variant_id || line.id) === String(model.variantId) && propertiesKey(line.properties) === propertiesKey(model.properties) && String(line.selling_plan_allocation?.selling_plan?.id || '') === String(model.sellingPlan || '');
const targetLines = (cart, id, original) => cart.items.filter(line => String(line.variant_id || line.id) === String(id) && propertiesKey(line.properties) === propertiesKey(original.properties) && String(line.selling_plan_allocation?.selling_plan?.id || '') === String(original.selling_plan_allocation?.selling_plan?.id || ''));
const total = lines => lines.reduce((sum, line) => sum + line.quantity, 0);
const usable = variant => variant.available;

function modelFor(picker) {
  // Horizon morphs existing nodes; parse the refreshed model rather than cache it.
  return JSON.parse(picker.querySelector('[data-np-variant-model]').textContent);
}
function message(text, error = false) {
  document.querySelectorAll('[data-np-cart-message]').forEach(node => {
    cancelAnimationFrame(node.npAnnounceFrame);
    node.setAttribute('role', error ? 'alert' : 'status');
    node.textContent = '';
    node.npAnnounceFrame = requestAnimationFrame(() => { node.textContent = text; });
  });
}
function pickVariant(variants, values, changedIndex) {
  const candidates = variants.filter(variant => usable(variant) && variant.options[changedIndex] === values[changedIndex]);
  // Keep all other selections whenever that combination exists. For a sparse matrix,
  // choose the closest real combination and visibly update every affected select.
  return candidates.sort((a, b) => {
    const score = variant => variant.options.reduce((sum, value, index) => sum + (value === values[index] ? 1 : 0), 0);
    return score(b) - score(a);
  })[0];
}
function selectVariant(picker, variant) {
  picker.querySelectorAll('[data-np-option-index]').forEach(select => {
    select.value = variant.options[Number(select.dataset.npOptionIndex)];
  });
}
async function getCart() {
  const response = await fetch(endpoint('cart.js'), { cache: 'no-store', credentials: 'same-origin', headers: { Accept: 'application/json' } });
  if (!response.ok) throw Error('Cart read failed');
  const cart = await response.json();
  if (!Array.isArray(cart.items)) throw Error('Invalid cart');
  return cart;
}
async function post(path, body) {
  const response = await fetch(endpoint(path), { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
  const result = await response.json();
  if (!response.ok) throw Error(typeof result.description === 'string' ? result.description : 'Cart update failed');
  return result;
}
function freezeCart() {
  const controls = Array.from(document.querySelectorAll('cart-items-component button, cart-items-component input, cart-items-component select'));
  const old = controls.map(control => [control, control.disabled]);
  controls.forEach(control => { control.disabled = true; });
  document.querySelectorAll('cart-items-component').forEach(component => component.setAttribute('aria-busy', 'true'));
  return () => {
    old.forEach(([control, disabled]) => { if (control.isConnected) control.disabled = disabled; });
    document.querySelectorAll('cart-items-component').forEach(component => component.removeAttribute('aria-busy'));
  };
}
async function replaceVariant(picker, target, model, optionIndex) {
  if (shared.pending) { message(model.messages.busy, true); selectVariant(picker, model.variants.find(variant => String(variant.id) === String(model.variantId))); return; }
  const release = freezeCart();
  let deferred;
  let mutationStarted = false;
  let completed = false;
  let finalCart;
  let sections;
  let errorMessage;
  const sectionIds = Array.from(new Set(Array.from(document.querySelectorAll('cart-items-component')).map(node => node.dataset.sectionId).filter(Boolean))).slice(0, 5);
  const savedFocus = { productId: String(model.productId), optionIndex };
  const run = async () => {
    message(model.messages.updating);
    try {
      const before = await getCart();
      const originals = before.items.filter(line => sameOriginal(line, model));
      if (originals.length !== 1) throw Error(model.messages.changed);
      const original = originals[0];
      const originalIdentity = identity(original);
      // Render current inventory and options from this actual cart section, not a
      // cached product JSON or an invented option-to-variant mapping.
      const stockUrl = new URL(location.pathname, location.origin);
      stockUrl.searchParams.set('section_id', picker.closest('cart-items-component').dataset.sectionId);
      stockUrl.searchParams.set('_np', String(Date.now()));
      const stockResponse = await fetch(stockUrl, { cache: 'no-store', credentials: 'same-origin' });
      if (!stockResponse.ok) throw Error(model.messages.unavailable);
      const doc = new DOMParser().parseFromString(await stockResponse.text(), 'text/html');
      const freshPicker = Array.from(doc.querySelectorAll('[data-np-cart-variants]')).find(node => node.dataset.lineKey === original.key);
      if (!freshPicker) throw Error(model.messages.changed);
      const fresh = JSON.parse(freshPicker.querySelector('[data-np-variant-model]').textContent);
      const currentTarget = fresh.variants.find(variant => String(variant.id) === String(target.id) && usable(variant));
      if (!currentTarget) throw Error(model.messages.unavailable);
      const targetBefore = total(targetLines(before, target.id, original));
      deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [{ merchandiseId: String(target.id), quantity: original.quantity }], promise: deferred.promise }));
      mutationStarted = true;
      const item = { id: Number(target.id), quantity: original.quantity, properties: { ...(original.properties || {}) } };
      const plan = original.selling_plan_allocation?.selling_plan?.id;
      if (plan) item.selling_plan = plan;
      await post('cart/add.js', { items: [item] });
      const afterAdd = await getCart();
      const originalNow = afterAdd.items.filter(line => identity(line) === originalIdentity);
      if (originalNow.length !== 1 || originalNow[0].quantity !== original.quantity || total(targetLines(afterAdd, target.id, original)) !== targetBefore + original.quantity) throw Error(model.messages.interrupted);
      // Only remove the old line after the replacement quantity is confirmed.
      // Re-read the key because automatic discounts can change line keys.
      const changed = await post('cart/change.js', { id: originalNow[0].key, quantity: 0, sections: sectionIds, sections_url: location.pathname });
      sections = changed.sections;
      finalCart = await getCart();
      if (finalCart.items.some(line => identity(line) === originalIdentity) || total(targetLines(finalCart, target.id, original)) !== targetBefore + original.quantity) throw Error(model.messages.interrupted);
      completed = true;
    } catch (error) {
      errorMessage = mutationStarted ? model.messages.interrupted : (error.message || model.messages.failed);
      // Never repeat or roll back an uncertain write: that could delete a line
      // another tab changed. Refresh the actual cart and explain the ambiguity.
      try { finalCart = await getCart(); } catch { /* Native components will refetch. */ }
    } finally {
      if (deferred) {
        if (finalCart) {
          deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(finalCart), detail: { source: 'np-cart-variant', sourceId: 'np-cart-variant', items: finalCart.items, itemCount: finalCart.item_count, sections: completed ? sections : undefined, didError: !completed } });
        } else deferred.reject(Error(model.messages.failed));
      }
      if (!completed) {
        const originalVariant = model.variants.find(variant => String(variant.id) === String(model.variantId));
        if (originalVariant && picker.isConnected) selectVariant(picker, originalVariant);
      }
    }
  };
  const operation = navigator.locks ? navigator.locks.request('niceprimes-free-hat-claim', run) : run();
  shared.pending = operation;
  try { await operation; }
  finally {
    if (shared.pending === operation) shared.pending = null;
    release();
    // Existing gift components may have rendered while this shared lock was held.
    document.querySelectorAll('[data-np-gift-block]').forEach(gift => { if (gift.connected && gift.state && typeof gift.render === 'function') gift.render(gift.state); });
    message(completed ? model.messages.updated.replace('__NP_VARIANT__', target.options.join(' / ')) : errorMessage || model.messages.failed, !completed);
    // Morph can destroy the focused select. Restore focus only in that case.
    if (!document.activeElement || document.activeElement === document.body) {
      requestAnimationFrame(() => {
        const replacement = Array.from(document.querySelectorAll('[data-np-cart-variants]')).find(node => {
          if (node.dataset.productId !== savedFocus.productId) return false;
          const refreshed = modelFor(node);
          return String(refreshed.variantId) === String(completed ? target.id : model.variantId) && propertiesKey(refreshed.properties) === propertiesKey(model.properties) && String(refreshed.sellingPlan || '') === String(model.sellingPlan || '');
        });
        replacement?.querySelector('[data-np-option-index="' + savedFocus.optionIndex + '"]')?.focus({ preventScroll: true });
      });
    }
  }
}

document.addEventListener('change', event => {
  const select = event.target.closest?.('[data-np-option-index]');
  if (!select) return;
  const picker = select.closest('[data-np-cart-variants]');
  if (!picker) return;
  const model = modelFor(picker);
  const selects = Array.from(picker.querySelectorAll('[data-np-option-index]'));
  const values = selects.map(input => input.value);
  const index = Number(select.dataset.npOptionIndex);
  const variant = pickVariant(model.variants, values, index);
  if (!variant) { message(model.messages.unavailable, true); return; }
  selectVariant(picker, variant);
  if (String(variant.id) === String(model.variantId)) return;
  replaceVariant(picker, variant, model, index).catch(() => message(model.messages.failed, true));
});

// Checkout submission guard: keep the native submitter enabled so its checkout command is sent.
let npCheckoutPending = false;
let npCartRequests = 0;
function resetNpCheckout() {
  npCheckoutPending = false;
  document.querySelectorAll('#cart-drawer [data-np-checkout-busy]').forEach(button => {
    button.removeAttribute('aria-disabled');
    button.removeAttribute('aria-busy');
  });
}
function trackNpCartRequest(event) {
  if (!event.promise || typeof event.promise.then !== 'function') return;
  npCartRequests++;
  Promise.resolve(event.promise).then(
    () => { npCartRequests--; },
    () => { npCartRequests--; }
  );
}
function guardNpCheckout(event) {
  const button = event.submitter;
  if (event.target?.id !== 'cart-form' || button?.name !== 'checkout' || !button.closest('#cart-drawer')) return;
  if (event.defaultPrevented) return;
  if (button.disabled || npCheckoutPending) { event.preventDefault(); return; }
  if (shared.pending || npCartRequests > 0) {
    event.preventDefault();
    message(button.dataset.npCheckoutBusy, true);
    return;
  }
  npCheckoutPending = true;
  button.setAttribute('aria-disabled', 'true');
  button.setAttribute('aria-busy', 'true');
  // A different listener may cancel navigation after ours; then let the customer try again.
  Promise.resolve().then(() => { if (event.defaultPrevented) resetNpCheckout(); });
}
document.addEventListener('submit', guardNpCheckout);
for (const type of [StandardEvents.cartLinesUpdate, StandardEvents.cartDiscountUpdate, StandardEvents.cartNoteUpdate]) {
  document.addEventListener(type, trackNpCartRequest);
}
window.addEventListener('pageshow', resetNpCheckout);

// Use Horizon's existing product-details dialog rather than navigating away from the cart.
let npDetailsLoading = false;
class NpCartProductDetails extends QuickAddComponent {
  get productPageUrl() { return this.dataset.productUrl || ''; }
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.openCartDetails);
  }
  disconnectedCallback() {
    this.removeEventListener('click', this.openCartDetails);
    super.disconnectedCallback();
  }
  async fetchProductPage(url) {
    const html = await super.fetchProductPage(url);
    if (!html?.querySelector('[data-product-grid-content]')) throw Error('Product details unavailable');
    return html;
  }
  openCartDetails = async event => {
    const button = event.target.closest?.('button.np-cart-option-link');
    if (!button || !this.contains(button)) return;
    event.preventDefault();
    if (npDetailsLoading) return;
    npDetailsLoading = true;
    const status = this.querySelector('[data-np-cart-message]');
    if (status) status.textContent = '';
    button.setAttribute('aria-disabled', 'true');
    button.setAttribute('aria-busy', 'true');
    try {
      if (!document.querySelector('#quick-add-dialog')) throw Error('Product dialog unavailable');
      await this.handleClick(event);
    } catch {
      if (status) status.textContent = this.dataset.errorMessage;
    } finally {
      npDetailsLoading = false;
      button.removeAttribute('aria-disabled');
      button.removeAttribute('aria-busy');
    }
  };
}
if (!customElements.get('np-cart-product-details')) customElements.define('np-cart-product-details', NpCartProductDetails);
